import React, { useRef, useState } from 'react';
import { Crosshair, Eye, Footprints, Ghost, Moon, Sparkles } from 'lucide-react';
import { COMBAT_PROWESS, DIMENSIONAL_TRAVEL, FATE, NIGHT_SPIRIT, NIGHT_SPIRIT_MERGE, TRUESIGHT, blinkStepsRemaining, canUseFate, canUsePeerlessAim, hasEpicBoon,
  inShadow } from '../../../shared/rules/epicBoons.mjs';
import { invisibleEffects } from '../../../shared/rules/effects/invisibility.mjs';
import { sendNightSpirit } from '../utils/epicBoonService';
import { notify } from '../../../utils/notification';

const newId = () => window.crypto?.randomUUID?.() || `boon-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * Epic Boon tiles (2024). Reminders (lit while ready, dim once used; clicking explains): Improve Fate, Peerless Aim and
 * Truesight. Actions: Blink Steps (opens map targeting through `onBlinkSteps`), the Night Spirit's shadow toggle and
 * Merge with Shadows (the server checks the shadow and the Bonus Action).
 */
export const epicBoonTiles = (form, characterId, combatState, isActiveTurn = false) => {
  const id = String(characterId);
  const inCombat = Number.isInteger(combatState?.turnSequence) && Number.isInteger(combatState?.activeTurn);
  const ledger = combatState?.turnActionLedger?.combatantId === id && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const fateReady = canUseFate(form);
  const aimReady = canUsePeerlessAim(form, combatState, id);
  const blinks = blinkStepsRemaining(form, ledger);
  const shadowed = inShadow(combatState, id);
  const merged = invisibleEffects(combatState, id).some(effect => effect.sourceFeature === NIGHT_SPIRIT_MERGE);
  const bonusReady = !inCombat || (isActiveTurn && !ledger?.bonusActionSpent);
  return [
    hasEpicBoon(form, FATE) && { id: 'fate', name: 'Improve Fate', Icon: Sparkles, accent: '#d6b4ff', lit: fateReady,
      value: fateReady ? 'Ready' : 'Used',
      about: `Boon of Fate: when one of your own d20 rolls misses or fails, add 2d4 to it (offered with the roll). ${fateReady
        ? 'Ready now.' : 'Used; it returns when you roll Initiative or finish a Short or Long Rest.'}` },
    hasEpicBoon(form, COMBAT_PROWESS) && { id: 'peerless-aim', name: 'Peerless Aim', Icon: Crosshair, accent: '#ff9f7a', lit: aimReady,
      value: !inCombat ? 'In combat' : aimReady ? 'Ready' : 'Used',
      about: `Boon of Combat Prowess: when you miss with an attack roll, you can hit instead (offered with the roll, on any turn). ${!inCombat
        ? 'Used in combat.' : aimReady ? 'Ready now.' : 'Used; it returns at the start of your next turn.'}` },
    hasEpicBoon(form, DIMENSIONAL_TRAVEL) && { id: 'blink-steps', name: 'Blink Steps', Icon: Footprints, accent: '#9ec9ff', action: 'blink',
      // Just the name: it lights when a teleport is ready (after the Attack or Magic action); the tooltip says the rest.
      lit: inCombat ? isActiveTurn && blinks > 0 : true, value: null,
      about: `Boon of Dimensional Travel: right after you take the Attack action or the Magic action, teleport up to 30 feet to an unoccupied space you can see. ${!inCombat
        ? 'Click to pick a square.' : blinks ? `${blinks} ready now: click to pick a highlighted square.` : 'Ready after your Attack or Magic action.'}` },
    hasEpicBoon(form, NIGHT_SPIRIT) && { id: 'shadow', name: 'In Shadow', Icon: Moon, accent: '#a79bff', action: shadowed ? 'shadow-off' : 'shadow-on', lit: shadowed,
      value: shadowed ? 'On' : 'Off',
      about: 'Boon of the Night Spirit: turn this on while you are in Dim Light or Darkness. Shadowy Form gives Resistance to all damage except Psychic and Radiant.' },
    hasEpicBoon(form, NIGHT_SPIRIT) && { id: 'merge', name: 'Merge with Shadows', Icon: Ghost, accent: '#a79bff', action: merged ? null : 'merge',
      lit: merged || (shadowed && bonusReady), value: merged ? 'Invisible' : 'Bonus Action',
      about: 'Boon of the Night Spirit: in Dim Light or Darkness, a Bonus Action gives you the Invisible condition until you take an action, a Bonus Action or a Reaction.' },
    hasEpicBoon(form, TRUESIGHT) && { id: 'truesight', name: 'Truesight', Icon: Eye, accent: '#8fe3d0', lit: true, value: '60 ft',
      about: 'Boon of Truesight: you have Truesight within 60 feet. Your DM marks the Invisible creatures you can see.' },
  ].filter(Boolean);
};

export default function EpicBoonTiles({ form, characterId, combatState, isActiveTurn = false, onBlinkSteps, onCombatStateReconciled }) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const tiles = epicBoonTiles(form, characterId, combatState, isActiveTurn);
  if (!tiles.length) return null;
  const act = async ({ action, name, about }) => {
    if (action === 'blink') return onBlinkSteps?.();
    if (!action) return notify(`${name}: ${about}`, 'info');
    if (lock.current) return undefined;
    lock.current = true; setBusy(true);
    try {
      const result = await sendNightSpirit(String(form?._id || characterId), { action, eventId: newId() });
      if (result.combat) onCombatStateReconciled?.(result.combat);
      notify(action === 'merge' ? 'Merge with Shadows · You are Invisible until you take an action, a Bonus Action or a Reaction.'
        : action === 'shadow-on' ? 'In Dim Light or Darkness · Shadowy Form resists all damage except Psychic and Radiant.' : 'Out of the shadow.', 'success');
    } catch (error) { notify(error.message, 'warning'); }
    finally { lock.current = false; setBusy(false); }
    return undefined;
  };
  return <>{tiles.map(tile => (
    <button key={tile.id} type="button" data-action-tile={`boon:${tile.id}`} aria-label={tile.value ? `${tile.name}: ${tile.value}` : tile.name} title={tile.about}
      className={`combat-hud-resource-tile combat-hud-feature-action-tile${tile.action === undefined ? ' combat-hud-resource-tile--passive' : ''}${tile.lit ? '' : ' combat-hud-resource-tile--dormant'}`}
      style={{ '--hud-resource-accent': tile.accent }} disabled={busy && tile.action !== undefined} onClick={() => act(tile)}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><tile.Icon size={20}/></span>
      <span className="combat-hud-resource-tile__name">{tile.name}</span>
      {tile.value && <span className="combat-hud-resource-tile__value">{tile.value}</span>}
    </button>
  ))}</>;
}
